import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import axiosInstance from "../../utils/axiosConfig"; // استخدم الـ axios instance الجديد

const API_URL = process.env.REACT_APP_API_URL;

// Thunks
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchAll',
  async ({ filter, page = 1, pageSize = 20 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/AllTransaction/?Days=${filter.days}&Status=${filter.status}&payment_method=${filter.paymentMethod}&page=${page}&page_size=${pageSize}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      console.log("API Response (All Transactions):", response.data);
      return {
        ...response.data,
        currentPage: page,
        pageSize: pageSize
      };
    } catch (error) {
      console.error('Fetch transactions error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const searchTransactions = createAsyncThunk(
  'transactions/search',
  async (query, { rejectWithValue }) => {
    try {
      console.log('Searching transactions with query:', query);
      const response = await axios.get(
        `${API_URL}/GetTransaction/search/?q=${encodeURIComponent(query)}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      console.log("API Response (Search):", response.data);
      // Return the first item from the array since the response is wrapped in an array
      return response.data[0];
    } catch (error) {
      console.error('Search transactions error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to search transactions');
    }
  }
);

export const getTransactionDetails = createAsyncThunk(
  'transactions/getDetails',
  async (id, { rejectWithValue }) => {
    try {
      console.log('Fetching transaction details for ID:', id);
      const response = await axios.get(`${API_URL}/GetTransaction/${id}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      console.log("API Response (Transaction Details):", response.data);
      return response.data[0];
    } catch (error) {
      console.error('Get transaction details error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transaction details');
    }
  }
);

const initialState = {
  data: null,
  searchResults: null,
  selectedTransaction: null,
  isLoading: false,
  isSearching: false,
  isLoadingDetails: false,
  error: null,
  searchError: null,
  detailsError: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  },
  searchPagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  },
  isSearchMode: false
};

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = null;
      state.searchError = null;
      state.isSearchMode = false;
      state.searchPagination = {
        currentPage: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
      };
    },
    clearSelectedTransaction: (state) => {
      state.selectedTransaction = null;
      state.detailsError = null;
    },
    setSearchMode: (state, action) => {
      state.isSearchMode = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        // Store the complete response data
        state.data = action.payload;
        state.error = null;
        state.pagination = {
          currentPage: action.payload.currentPage,
          pageSize: action.payload.pageSize,
          totalCount: action.payload.count,
          totalPages: Math.ceil(action.payload.count / action.payload.pageSize),
          hasNext: !!action.payload.next,
          hasPrevious: !!action.payload.previous
        };
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Search Transactions
      .addCase(searchTransactions.pending, (state) => {
        state.isSearching = true;
        state.searchError = null;
        state.isSearchMode = true;
      })
      .addCase(searchTransactions.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload;
        state.searchError = null;
        state.isSearchMode = true;
      })
      .addCase(searchTransactions.rejected, (state, action) => {
        state.isSearching = false;
        state.searchError = action.payload;
        state.isSearchMode = false;
      })
      // Get Transaction Details
      .addCase(getTransactionDetails.pending, (state) => {
        state.isLoadingDetails = true;
        state.detailsError = null;
      })
      .addCase(getTransactionDetails.fulfilled, (state, action) => {
        state.isLoadingDetails = false;
        state.selectedTransaction = action.payload;
        state.detailsError = null;
      })
      .addCase(getTransactionDetails.rejected, (state, action) => {
        state.isLoadingDetails = false;
        state.detailsError = action.payload;
      });
  }
});

export const { clearSearchResults, clearSelectedTransaction, setSearchMode } = transactionsSlice.actions;

// Selectors
export const selectTransactions = (state) => state.transactions.data;
export const selectSearchResults = (state) => state.transactions.searchResults;
export const selectSelectedTransaction = (state) => state.transactions.selectedTransaction;
export const selectLoadingStates = (state) => ({
  isLoading: state.transactions.isLoading,
  isSearching: state.transactions.isSearching,
  isLoadingDetails: state.transactions.isLoadingDetails
});
export const selectErrors = (state) => ({
  error: state.transactions.error,
  searchError: state.transactions.searchError,
  detailsError: state.transactions.detailsError
});
export const selectPagination = (state) => state.transactions.pagination;
export const selectSearchPagination = (state) => state.transactions.searchPagination;
export const selectIsSearchMode = (state) => state.transactions.isSearchMode;

export default transactionsSlice.reducer;